import { useEffect } from 'react';
import { create } from 'zustand';

// Reference count of mounted immersive page components. A count is used instead
// of a boolean so overlapping mounts (e.g. a fast A/B swap that briefly mounts
// two immersive variations) cannot leave the chrome hidden after the last one
// unmounts.
interface ImmersiveChromeState {
  count: number;
  acquire: () => void;
  release: () => void;
}

const useImmersiveChromeStore = create<ImmersiveChromeState>(set => ({
  count: 0,
  acquire: () => set(state => ({ count: state.count + 1 })),
  release: () => set(state => ({ count: Math.max(0, state.count - 1) })),
}));

/**
 * True while any immersive page component is mounted. Read by GlobalLayout to
 * hide the global header, promo bar, and footer.
 */
export const useImmersiveChromeActive = () => useImmersiveChromeStore(state => state.count > 0);

/**
 * Immersive page components (Free Trial FTUX, Dashboard Backdrop) call this to
 * hide the global chrome while they are mounted, regardless of how they were
 * rendered: A/B variation swapped in client-side, Builder editor preview, or a
 * plain SSR page. derivePageChrome can only see the SSR/parent variation, so it
 * misses immersive layouts that live in a child A/B variation or are injected by
 * the Builder editor. Tying the decision to the component actually rendering
 * closes those gaps.
 */
export const useHideGlobalChrome = () => {
  const acquire = useImmersiveChromeStore(state => state.acquire);
  const release = useImmersiveChromeStore(state => state.release);

  useEffect(() => {
    acquire();

    return () => release();
  }, [acquire, release]);
};
